import React, { useState } from 'react';
import { Filter, Eye, ExternalLink, X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from 'lucide-react';

const Portfolio: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState('tous');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageZoom, setImageZoom] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState<{ [key: number]: number }>({});

  const filters = [
    { id: 'tous', name: 'Tous les projets' },
    { id: 'interieur', name: 'Agencement Intérieur' },
    { id: 'exterieur', name: 'Agencement Extérieur' },
    { id: 'menuiserie', name: 'Menuiseries' },
    { id: 'professionnel', name: 'Projets Pro' }
  ];

  const projects = [
    {
      id: 1,
      title: 'Façade Restaurant "Les Embruns"',
      category: 'professionnel',
      type: 'Projet Professionnel',
      description: 'Rénovation complète de façade.',
      tags: ['Portes et Fenêtres', 'Sur Mesure', 'Professionnel', 'Façade'],
      images: ['/images/embrunts.jpg', '/images/embrunts2.jpg']
    },
    {
      id: 2,
      title: 'Agencement Salle de Bain',
      category: 'interieur',
      type: 'Agencement Intérieur',
      description: 'Optimisation d\'une salle de bain avec mobilier intégré et finitions haut de gamme.',
      tags: ['Salle de bain', 'Agencement', 'Mobilier'],
      images: ['/images/sdb.jpg', '/images/sdb2.jpg']
    },
    {
      id: 3,
      title: 'Appui de Fenêtre Véranda',
      category: 'interieur',
      type: 'Menuiserie Intérieure',
      description: 'Création d\'appuis de fenêtre personnalisés pour une véranda contemporaine.',
      tags: ['Appui', 'Véranda', 'Bois massif'],
      image: '/images/veranda.jpg'
    },
    {
      id: 4,
      title: 'Placard Sous Pente et Étagères Sur Mesure',
      category: 'interieur',
      type: 'Optimisation d\'Espace',
      description: 'Aménagement intelligent d\'un placard sous pente avec étagères et penderies personnalisées.',
      tags: ['Placard', 'Étagères', 'Sous pente', 'Sur mesure'],
      images: ['/images/placard.jpg', '/images/etgeres2.webp']
    },
    {
      id: 6,
      title: 'Portail Café Pod\' Bronnek',
      category: 'professionnel',
      type: 'Menuiserie Professionnelle',
      description: 'Création d\'un portail sur mesure pour l\'entrée du café, alliant esthétique et sécurité.',
      tags: ['Portail', 'Professionnel', 'Sur mesure', 'Bois exotique'],
      image: '/images/pod1.jpg'
    },
    {
      id: 7,
      title: 'Banc Extérieur Bois Exotique',
      category: 'professionnel',
      type: 'Mobilier Extérieur',
      description: 'Banc extérieur mêlant pierre locale et bois, parfaitement intégré au café.',
      tags: ['Banc', 'Extérieur', 'Bois exotique'],
      image: '/images/pod2.jpg'
    },
    {
      id: 8,
      title: 'Baie vitrée Sur Mesure',
      category: 'menuiserie',
      type: 'Menuiserie Sur Mesure',
      description: 'Baie vitrée coulissante en PVC, alliant élégance et performance thermique.',
      tags: ['Porte-fenêtre', 'PVC', 'Sur mesure'],
      image: '/images/portefenetre.webp'
    },
    {
      id: 9,
      title: 'Porte Extérieure',
      category: 'menuiserie',
      type: 'Menuiserie Extérieure',
      description: 'Porte extérieure en aluminium avec finitions soignées.',
      tags: ['Porte', 'Aluminium', 'Extérieur'],
      image: '/images/porte.webp'
    },
    {
      id: 10,
      title: 'Terrasse Bois Exotique',
      category: 'exterieur',
      type: 'Agencement Extérieur',
      description: 'Terrasse en cumaru, bois exotique robuste et durable.',
      tags: ['Terrasse', 'Cumaru', 'Extérieur'],
      image: '/images/terrasse.jpg'
    },
    {
      id: 11,
      title: 'Terrasse Bois',
      category: 'exterieur',
      type: 'Agencement Extérieur',
      description: 'Terrasse en pin classe 4 avec finitions de qualité.',
      tags: ['Terrasse', 'Bois', 'Extérieur'],
      images: ['/images/terasse3.jpg', '/images/terasse2.webp']
    },
    {
      id: 12,
      title: 'Terrasse en Pin',
      category: 'exterieur',
      type: 'Agencement Extérieur',
      description: 'Terrasse en pin naturel avec structure renforcée et finitions soignées.',
      tags: ['Terrasse', 'Pin', 'Extérieur', 'Bois'],
      images: ['/images/terrasse4_1.jpg', '/images/terrasse4_2.jpg']
    },
    {
      id: 13,
      title: 'Restauration Auvent Traditionnel',
      category: 'exterieur',
      type: 'Rénovation Patrimoine',
      description: 'Restauration d\'un auvent traditionnel en bois. Intervention sur cet auvent dont le poteau était pourri à sa base. Les deux linteaux (poutres) supérieurs étaient également dégradés et ont été remplacés à l\'identique en chêne produit par une scierie locale pour redonner solidité et cachet à l\'entrée.',
      tags: ['Auvent', 'Chêne', 'Restauration', 'Patrimoine'],
      images: ['/images/poutre2neuve.jpg', '/images/poutre1.jpg']
    },
    {
      id: 14,
      title: 'Changement de Volet',
      category: 'menuiserie',
      type: 'Menuiserie Extérieure',
      description: 'Remplacement de volet avec finitions soignées et adaptation sur mesure.',
      tags: ['Volet', 'Menuiserie', 'Extérieur'],
      image: '/images/volet1.jpeg'
    },
    {
      id: 15,
      title: 'Pose de Lambris Intérieur',
      category: 'interieur',
      type: 'Agencement Intérieur',
      description: 'Installation de lambris bois pour sols, murs et plafonds. Revêtement chaleureux et élégant.',
      tags: ['Lambris', 'Bois', 'Revêtement'],
      image: '/images/lambris1.jpg'
    }
  ];

  const filteredProjects = activeFilter === 'tous'
    ? projects
    : projects.filter(project => project.category === activeFilter);

  // Fonction helper pour obtenir l'image courante d'un projet
  const getCurrentImage = (project: any) => {
    if (project.images) {
      const index = currentImageIndex[project.id] || 0;
      return project.images[index];
    }
    return project.image;
  };

  // Fonction pour changer d'image dans une galerie
  const changeImage = (projectId: number, direction: 'next' | 'prev', totalImages: number) => {
    setCurrentImageIndex(prev => {
      const currentIndex = prev[projectId] || 0;
      let newIndex;
      if (direction === 'next') {
        newIndex = (currentIndex + 1) % totalImages;
      } else {
        newIndex = currentIndex === 0 ? totalImages - 1 : currentIndex - 1;
      }
      return { ...prev, [projectId]: newIndex };
    });
  };

  return (
    <section id="realisations" className="py-20 bg-white">
      <div className="container-max section-padding">
        {/* En-tête */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-6">
            Nos <span className="text-wood-600">Réalisations</span>
          </h2>
          <p className="text-xl text-stone-600 max-w-3xl mx-auto mb-8">
            Découvrez une sélection de nos projets les plus représentatifs. 
            Chaque réalisation témoigne de notre savoir-faire et de notre attention aux détails.
          </p>

          {/* Filtres */}
          <div className="flex flex-wrap justify-center gap-3">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  activeFilter === filter.id
                    ? 'bg-wood-600 text-white'
                    : 'bg-stone-100 text-stone-700 hover:bg-wood-50 hover:text-wood-600'
                }`}
              >
                <Filter className="w-4 h-4" />
                <span>{filter.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Grille des projets */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="group bg-white rounded-lg shadow-lg overflow-hidden border border-stone-200 hover:shadow-xl transition-all duration-300"
            >
              {/* Image */}
              <div className="aspect-[4/3] bg-gradient-to-br from-stone-100 to-stone-200 relative overflow-hidden">
                {getCurrentImage(project) ? (
                  <img 
                    src={getCurrentImage(project)}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      // Fallback si l'image ne charge pas
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      target.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                
                {/* Fallback si pas d'image */}
                <div className={`w-full h-full flex items-center justify-center text-stone-600 ${getCurrentImage(project) ? 'hidden' : ''}`}>
                  <div className="text-center space-y-2">
                    <div className="w-16 h-16 bg-wood-600 rounded-lg mx-auto opacity-80 flex items-center justify-center">
                      <Eye className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-sm font-medium">Photo du projet</p>
                  </div>
                </div>

                {/* Indicateur de galerie */}
                {project.images && project.images.length > 1 && (
                  <div className="absolute top-3 right-3 bg-black bg-opacity-70 text-white px-3 py-1 rounded-full text-sm font-medium">
                    {((currentImageIndex[project.id] || 0) + 1)} / {project.images.length}
                  </div>
                )}

                {/* Boutons de navigation pour la galerie */}
                {project.images && project.images.length > 1 && (
                  <>
                    <button
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white p-2 rounded-full transition-all z-10"
                      onClick={(e) => {
                        e.stopPropagation();
                        changeImage(project.id, 'prev', project.images.length);
                      }}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white p-2 rounded-full transition-all z-10"
                      onClick={(e) => {
                        e.stopPropagation();
                        changeImage(project.id, 'next', project.images.length);
                      }}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}
                
                {/* Overlay au hover */}
                <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <button 
                    className="bg-white text-stone-900 px-4 py-2 rounded-lg font-medium flex items-center space-x-2 hover:bg-stone-100 transition-colors"
                    onClick={() => {
                      const currentImg = getCurrentImage(project);
                      if (currentImg) {
                        setSelectedImage(currentImg);
                        setImageZoom(1);
                      }
                    }}
                  >
                    <Eye className="w-4 h-4" />
                    <span>Voir la photo</span>
                  </button>
                </div>
              </div>

              {/* Contenu */}
              <div className="p-6">
                <div className="mb-3">
                  <span className="inline-block bg-wood-100 text-wood-700 text-xs font-medium px-2 py-1 rounded">
                    {project.type}
                  </span>
                </div>
                
                <h3 className="text-xl font-bold text-stone-900 mb-3">
                  {project.title}
                </h3>
                
                <p className="text-stone-600 mb-4 leading-relaxed">
                  {project.description}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-sage-100 text-sage-700 text-xs px-2 py-1 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between">
                  <button 
                    className="text-wood-600 hover:text-wood-700 font-medium flex items-center space-x-2"
                    onClick={() => {
                      const currentImg = getCurrentImage(project);
                      if (currentImg) {
                        setSelectedImage(currentImg);
                        setImageZoom(1);
                      }
                    }}
                  >
                    <span>Voir {project.images && project.images.length > 1 ? 'les photos' : 'la photo'}</span>
                    <ExternalLink className="w-4 h-4" />
                  </button>
                  <button 
                    className="bg-stone-100 hover:bg-stone-200 text-stone-700 p-2 rounded-lg transition-colors"
                    onClick={() => {
                      const currentImg = getCurrentImage(project);
                      if (currentImg) {
                        setSelectedImage(currentImg);
                        setImageZoom(1);
                      }
                    }}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Section témoignages */}
        <div className="mt-20">
          <h3 className="text-2xl font-bold text-stone-900 text-center mb-12">
            Ce que disent nos clients
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Marie Dubois',
                project: 'Agencement cuisine',
                rating: 5,
                comment: 'Un travail exceptionnel ! Jérémy a su créer exactement la cuisine de nos rêves.'
              },
              {
                name: 'Pierre Martin',
                project: 'Terrasse composite',
                rating: 5,
                comment: 'Professionnel, ponctuel et soucieux du détail. Je recommande vivement !'
              },
              {
                name: 'Sophie Leroux',
                project: 'Dressing sur mesure',
                rating: 5,
                comment: 'Une optimisation parfaite de l\'espace. Un artisan à l\'écoute et créatif.'
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-stone-50 p-6 rounded-lg border border-stone-200">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-wood-500 text-lg">★</span>
                  ))}
                </div>
                <p className="text-stone-600 mb-4 italic">
                  "{testimonial.comment}"
                </p>
                <div>
                  <div className="font-semibold text-stone-900">{testimonial.name}</div>
                  <div className="text-sm text-stone-500">{testimonial.project}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to action */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-sage-100 to-sage-200 rounded-2xl p-8 md:p-12">
            <h3 className="text-2xl md:text-3xl font-bold text-stone-900 mb-4">
              Votre projet mérite notre expertise
            </h3>
            <p className="text-lg text-stone-600 mb-8">
              Chaque réalisation est unique. Parlons ensemble de votre projet 
              pour créer un espace qui vous ressemble.
            </p>
            <button 
              className="btn-primary"
              onClick={() => {
                const section = document.getElementById('contact');
                section?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Démarrer mon projet
            </button>
          </div>
        </div>
      </div>

      {/* Modal pour afficher l'image */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 overflow-auto p-4"
          onClick={() => setSelectedImage(null)}
        >
          {/* Boutons de contrôle fixes */}
          <div className="fixed top-4 right-4 z-20 flex space-x-2">
            <button
              className="bg-black bg-opacity-70 hover:bg-opacity-90 text-white p-3 rounded-lg transition-all shadow-lg border border-white border-opacity-20"
              onClick={(e) => {
                e.stopPropagation();
                setImageZoom(prev => Math.max(0.5, prev - 0.25));
              }}
              title="Dézoomer"
            >
              <ZoomOut className="w-5 h-5" />
            </button>
            <button
              className="bg-black bg-opacity-70 hover:bg-opacity-90 text-white p-3 rounded-lg transition-all shadow-lg border border-white border-opacity-20"
              onClick={(e) => {
                e.stopPropagation();
                setImageZoom(prev => Math.min(3, prev + 0.25));
              }}
              title="Zoomer"
            >
              <ZoomIn className="w-5 h-5" />
            </button>
            <button
              className="bg-black bg-opacity-70 hover:bg-opacity-90 text-white p-3 rounded-lg transition-all shadow-lg border border-white border-opacity-20"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage(null);
              }}
              title="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Indicateur de zoom fixe */}
          <div className="fixed bottom-4 left-4 z-20 bg-black bg-opacity-70 text-white px-4 py-2 rounded-lg text-sm shadow-lg border border-white border-opacity-20">
            Zoom: {Math.round(imageZoom * 100)}%
          </div>
          
          {/* Conteneur centré avec scroll */}
          <div className="min-h-full flex items-center justify-center py-8">
            <img
              src={selectedImage}
              alt="Projet en détail"
              className="transition-transform duration-200 cursor-zoom-in"
              style={{ 
                transform: `scale(${imageZoom})`,
                transformOrigin: 'center center',
                maxWidth: '100%',
                height: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default Portfolio;
